import UNIVERSITIES from "../data/universities.json";
import { EMPTY_KEYS, REGION_TO_CODE, UNI_IMG } from "./mapers";

const imagePreloadCache = new Map();

export const EMPTY_ARRAY = Object.freeze([]);

export const toArray = (value) => (Array.isArray(value) ? value : []);

export const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const cleanRegionKey = (value) =>
  String(value ?? "").trim().toUpperCase() || "—";

export const valueOrDash = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

export const plural = (value, singular, pluralLabel) =>
  Number(value) === 1 ? singular : pluralLabel;

export function isMissingValue(value) {
  const normalized = normalizeText(value);
  return (
    !normalized ||
    ["—", "-", "sin informacion", "sin dato"].includes(normalized)
  );
}

export function normalizeContentFilter(value) {
  const filtered = (Array.isArray(value) ? value : [value])
    .map(normalizeText)
    .filter(Boolean);

  return filtered.length ? filtered : ["general"];
}

export function safeDate(value) {
  const date = value ? new Date(value) : null;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
}

export function formatDate(value, locale = "es-CL") {
  return (
    safeDate(value)?.toLocaleDateString(locale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }) ?? "—"
  );
}

export function getItemYear(item) {
  const explicitYear = Number(item?.anio ?? item?.año ?? item?.year);
  if (Number.isFinite(explicitYear) && explicitYear > 0) return explicitYear;

  return (
    safeDate(
      item?.inicio ??
        item?.start_date ??
        item?.fecha ??
        item?.date ??
        item?.event_date
    )?.getFullYear() ?? null
  );
}

export function isWithinYearRange(item, yearFrom, yearTo) {
  const year = getItemYear(item);

  if (!yearFrom && !yearTo) return true;
  if (!year) return false;
  if (yearFrom && year < Number(yearFrom)) return false;
  if (yearTo && year > Number(yearTo)) return false;

  return true;
}

export function sortNames(list) {
  return [...toArray(list)].sort((a, b) =>
    String(a).localeCompare(String(b), "es", { sensitivity: "base" })
  );
}

export function uniqueSorted(list) {
  return sortNames([...new Set(toArray(list).filter(Boolean))]);
}

export function groupBy(rows, keyFn, fallback = "—") {
  const map = new Map();

  toArray(rows).forEach((row) => {
    const key = String(keyFn(row) ?? fallback).trim() || fallback;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });

  return map;
}

export function countToArray(rows, keyFn, fallback = "—") {
  const map = new Map();

  toArray(rows).forEach((row) => {
    const key = String(keyFn(row) ?? fallback).trim() || fallback;
    map.set(key, (map.get(key) ?? 0) + 1);
  });

  return Array.from(map.entries())
    .map(([id, value]) => ({ id, value }))
    .sort((a, b) => b.value - a.value);
}

export function countManyToArray(rows, keysFn) {
  const map = new Map();

  toArray(rows).forEach((row) => {
    const keys = Array.isArray(keysFn(row)) ? keysFn(row) : [];

    [...new Set(keys.map((value) => String(value ?? "").trim()))]
      .filter((key) => key && !EMPTY_KEYS.has(key.toUpperCase()))
      .forEach((key) => map.set(key, (map.get(key) ?? 0) + 1));
  });

  return Array.from(map.entries())
    .map(([id, value]) => ({ id, value }))
    .sort((a, b) => b.value - a.value);
}

export function freqBy(rows, keyOrFn, naLabel = "Sin información") {
  const get =
    typeof keyOrFn === "function" ? keyOrFn : (row) => row?.[keyOrFn];
  const map = new Map();

  toArray(rows).forEach((row) => {
    const raw = get(row);
    const key = String(raw ?? "").trim() || naLabel;
    map.set(key, (map.get(key) ?? 0) + 1);
  });

  return Array.from(map.entries())
    .map(([id, value]) => ({ id, value }))
    .sort((a, b) => b.value - a.value);
}

export function regionFreqToCode(
  freqRows,
  { ignore = ["—", "Sin información"] } = {}
) {
  const ignored = new Set(ignore.map((value) => String(value).toUpperCase()));

  return toArray(freqRows)
    .map((row) => {
      const roman = cleanRegionKey(row.id);
      const region = REGION_TO_CODE[roman];

      if (ignored.has(roman) || !region) return null;

      return {
        id: String(region.code),
        value: row.value,
        name: region.name,
        short: roman,
      };
    })
    .filter(Boolean);
}

export function getCount(rows, idOrFn) {
  const row =
    typeof idOrFn === "function"
      ? toArray(rows).find(idOrFn)
      : toArray(rows).find((item) => item.id === idOrFn);

  return row?.count || 0;
}

export function getList(rows, idOrFn) {
  const row =
    typeof idOrFn === "function"
      ? toArray(rows).find(idOrFn)
      : toArray(rows).find((item) => item.id === idOrFn);

  return row?.list || [];
}

export function formatTitle(text) {
  if (!text) return "";

  const normalized = String(text).trim().toLowerCase();
  const fixedLabels = {
    magister: "Magíster",
    licenciatura: "Licenciatura",
    doctorado: "Doctorado",
    matematica: "Matemática",
  };

  if (fixedLabels[normalized]) return fixedLabels[normalized];

  const lowerWords = new Set([
    "de",
    "del",
    "la",
    "las",
    "el",
    "los",
    "y",
    "e",
    "en",
    "a",
    "por",
    "para",
    "con",
    "sin",
    "o",
    "u",
  ]);

  return normalized
    .split(" ")
    .map((word, index) =>
      lowerWords.has(word) && index !== 0
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}

export function getUniversityData(value) {
  const raw = String(value ?? "").trim();
  const key = raw.toUpperCase();
  const formattedFallbackName =
  raw.includes(" ") &&
  (raw === raw.toUpperCase() || raw === raw.toLowerCase())
    ? formatTitle(raw)
    : raw;

  if (!key || EMPTY_KEYS.has(key)) return null;

  const data = UNIVERSITIES?.[key];

  if (typeof data === "string") {
    return {
      key,
      name: data,
      country: "—",
      is_cuech: false,
      is_cruch: false,
    };
  }

  return {
    key,
    name: String(data?.name ?? data?.nombre ?? formattedFallbackName).trim() || formattedFallbackName,
    country: String(data?.country ?? data?.pais ?? "—").trim() || "—",
    is_cuech: data?.is_cuech === true,
    is_cruch: data?.is_cruch === true,
  };
}

export function getUniversityFullName(value) {
  const raw = String(value ?? "").trim();
  if (raw.toUpperCase() === "OTHER") return "Otra universidad";

  const university = getUniversityData(raw);
  return university?.name ?? (isMissingValue(raw) ? "—" : raw);
}

export function universityImage(universityName) {
  const key = String(universityName ?? "").trim().replace(/\s+/g, " ");
  const image = UNI_IMG[key];

  return image
    ? `/assets/universities/${image.folder}/${image.file}`
    : null;
}

function preloadImage(src) {
  if (!src || typeof window === "undefined") return Promise.resolve(src || null);
  if (imagePreloadCache.has(src)) return imagePreloadCache.get(src);

  const promise = new Promise((resolve) => {
    const image = new window.Image();
    image.decoding = "async";
    image.onload = () => resolve(src);
    image.onerror = () => resolve(null);
    image.src = src;

    if (image.complete) resolve(src);
  });

  imagePreloadCache.set(src, promise);
  return promise;
}

export function preloadImages(sources) {
  return Promise.all([...new Set(toArray(sources).filter(Boolean))].map(preloadImage));
}
